import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type { THotTopicOverviewItemDTO } from '@conversed/rest/src/contracts/train-ai/train-ai.dto';
import { HOT_TOPIC_OVERVIEW_WINDOW_DAYS } from '@conversed/shared/src/central/constants/hot-topics';
import { LANGUAGE_FLAG_EMOJIS } from '@conversed/shared/src/central/constants/languages';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { getHotTopicDisplayName } from '@helpers/hotTopicHelper';
import clsx from 'clsx';
import type React from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { TTranslationState } from './conversation-review-modal.type';
import { StatChip } from './StatChip';

type Props = {
  topic: THotTopicOverviewItemDTO;
  topMatchScore: number | null;
  translationState: TTranslationState | null;
  allTopics: THotTopicOverviewItemDTO[];
  currentConversationTopicId: string | undefined;
  isActionDisabled: boolean;
  onViewRelated: () => void;
  onReassignHotTopic: (newTopicId: string) => void;
};

export const ConversationReviewStatsHeader: React.FC<Props> = ({
  topic,
  topMatchScore,
  translationState,
  allTopics,
  currentConversationTopicId,
  isActionDisabled,
  onViewRelated,
  onReassignHotTopic,
}) => {
  const { t } = useTranslation();

  const selectedTopicId = currentConversationTopicId ?? topic.id;
  const activeTopics = allTopics.filter((topicItem) => !topicItem.isDeleted);
  // A pending reassignment exists when the reviewer has selected a topic different from the
  // modal's originating hot topic. The API call is deferred until "Save & Continue".
  const isPendingReassignment = selectedTopicId !== topic.id;
  const selectedTopicRawName =
    allTopics.find((topicItem) => topicItem.id === selectedTopicId)?.name ?? '';
  const selectedTopicName = getHotTopicDisplayName(selectedTopicRawName);

  return (
    <div className='conversation-review-modal__stats-wrapper'>
      {translationState && (
        <div className='conversation-review-modal__translation-badge'>
          <span className='conversation-review-modal__translation-badge-text'>
            <span>
              {LANGUAGE_FLAG_EMOJIS[translationState.sourceLang] ?? '🌐'}{' '}
            </span>
            <span>
              {t('train-ai.conversation-review.language.originally-in', {
                language: translationState.sourceLanguageName,
              })}
            </span>
          </span>
        </div>
      )}
      <div className='conversation-review-modal__stats'>
        <StatChip
          label={t('train-ai.conversation-review.stats.questions-per-week', {
            count: topic.volume7d,
            days: HOT_TOPIC_OVERVIEW_WINDOW_DAYS,
          })}
        />
        {topMatchScore !== null && (
          <StatChip
            label={`${topMatchScore}% ${t('train-ai.conversation-review.stats.accuracy')}`}
          />
        )}
        <ButtonT
          className='!shadow-none'
          type='button'
          styling='outline-amber'
          size='pico'
          rounding='full'
          disabled={isActionDisabled}
          onClick={onViewRelated}>
          <span className='conversation-review-modal__view-related-inner'>
            🔥 {t('train-ai.conversation-review.stats.view-related')}
            <MaterialIcon
              icon='arrow_forward'
              classes={['text-tiny', 'leading-none']}
            />
          </span>
        </ButtonT>
        <Menu as='div' className='conversation-review-modal__reassign'>
          {({ open }) => (
            <>
              <MenuButton
                disabled={isActionDisabled}
                className={clsx(
                  'conversation-review-modal__reassign-button',
                  isActionDisabled &&
                    'conversation-review-modal__reassign-button--disabled',
                )}>
                <span className='conversation-review-modal__reassign-button-inner'>
                  <MaterialIcon
                    icon='shuffle'
                    classes={['text-tiny', 'leading-none']}
                  />
                  {isPendingReassignment ? (
                    <>
                      {t(
                        'train-ai.conversation-review.stats.reassign-pending-prefix',
                      )}
                      <MaterialIcon
                        icon='arrow_forward'
                        classes={['text-tiny', 'leading-none']}
                      />
                      <strong>{selectedTopicName}</strong>
                    </>
                  ) : (
                    t('train-ai.conversation-review.stats.reassign')
                  )}
                  <MaterialIcon
                    icon='expand_more'
                    classes={[
                      'conversation-review-modal__reassign-button-arrow',
                      open ? 'rotate-180' : '',
                    ]}
                  />
                </span>
              </MenuButton>
              <Transition as={Fragment}>
                <MenuItems
                  anchor='bottom start'
                  className='conversation-review-modal__reassign-menu'>
                  <span className='conversation-review-modal__reassign-menu-heading'>
                    {t('train-ai.conversation-review.stats.reassign-heading')}
                  </span>
                  <div className='conversation-review-modal__reassign-menu-items'>
                    {activeTopics.map((topicItem) => (
                      <MenuItem key={topicItem.id}>
                        <button
                          type='button'
                          className={clsx(
                            'conversation-review-modal__reassign-menu-item',
                            selectedTopicId === topicItem.id &&
                              'conversation-review-modal__reassign-menu-item--selected',
                          )}
                          onClick={() => onReassignHotTopic(topicItem.id)}>
                          <span className='conversation-review-modal__reassign-menu-item-name'>
                            {getHotTopicDisplayName(topicItem.name)}
                          </span>
                          <span className='conversation-review-modal__reassign-menu-item-volume'>
                            {topicItem.volume7d}/wk
                          </span>
                        </button>
                      </MenuItem>
                    ))}
                  </div>
                </MenuItems>
              </Transition>
            </>
          )}
        </Menu>
      </div>
    </div>
  );
};
